import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Rocket, ShoppingCart, Calculator, Globe, FileText, BarChart3, TrendingUp, Receipt } from 'lucide-react';

const float = (delay = 0, duration = 3, y = 10) => ({
  y: [-y, y, -y],
  transition: { duration, repeat: Infinity, ease: 'easeInOut', delay },
});

const pulse = (delay = 0) => ({
  scale: [1, 1.08, 1],
  transition: { duration: 4, repeat: Infinity, ease: 'easeInOut', delay },
});

const HeroIllustration = () => {
  const { i18n } = useTranslation();
  const en = i18n.language === 'en';
  return (
  <div className="relative w-full h-[500px]">
    {/* Central Laptop */}
    <motion.div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-48"
      animate={float(0, 4, 6)}
    >
      <div className="bg-slate-800 rounded-t-xl p-3 shadow-2xl">
        <div className="bg-indigo-950 rounded-lg h-32 flex items-end gap-2 p-4">
          {[60, 85, 45, 70, 90, 55, 75].map((h, i) => (
            <motion.div
              key={i}
              className="flex-1 rounded-t"
              style={{ height: `${h}%`, background: i % 2 === 0 ? '#818cf8' : '#6366f1' }}
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.8, delay: 0.3 + i * 0.1, ease: 'easeOut' }}
            />
          ))}
        </div>
      </div>
      <div className="bg-slate-700 h-2 rounded-b-xl mx-4" />
      <div className="bg-slate-600 h-1 rounded-b-xl mx-12" />
    </motion.div>

    {/* Floating Icons */}
    <motion.div
      className="absolute top-4 right-12 bg-white rounded-2xl shadow-lg p-4 border border-slate-100"
      animate={float(0.5, 3.5, 12)}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Rocket className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">Startup</p>
          <p className="text-[10px] text-slate-400">{en ? 'Launch business' : 'Avvio attività'}</p>
        </div>
      </div>
    </motion.div>

    <motion.div
      className="absolute top-16 left-0 bg-white rounded-2xl shadow-lg p-4 border border-slate-100"
      animate={float(1, 4, 10)}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
          <ShoppingCart className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">E-commerce</p>
          <p className="text-[10px] text-slate-400">{en ? 'Online business' : 'Business online'}</p>
        </div>
      </div>
    </motion.div>

    <motion.div
      className="absolute bottom-24 left-4 bg-white rounded-2xl shadow-lg p-4 border border-slate-100"
      animate={float(0.3, 3.2, 14)}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
          <Calculator className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">{en ? 'Tax' : 'Fiscale'}</p>
          <p className="text-[10px] text-slate-400">{en ? 'VAT & Income Tax' : 'IVA & IRPEF'}</p>
        </div>
      </div>
    </motion.div>

    <motion.div
      className="absolute bottom-12 right-0 bg-white rounded-2xl shadow-lg p-4 border border-slate-100"
      animate={float(0.8, 3.8, 11)}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
          <Globe className="w-5 h-5 text-sky-600" />
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">{en ? 'International' : 'Internazionale'}</p>
          <p className="text-[10px] text-slate-400">{en ? 'Foreign taxation' : 'Fiscalità estera'}</p>
        </div>
      </div>
    </motion.div>

    <motion.div
      className="absolute top-1/3 right-0 bg-white rounded-2xl shadow-lg p-3 border border-slate-100"
      animate={float(1.2, 3, 8)}
    >
      <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center">
        <FileText className="w-5 h-5 text-violet-600" />
      </div>
    </motion.div>

    <motion.div
      className="absolute top-1/4 left-8 bg-white rounded-2xl shadow-lg p-3 border border-slate-100"
      animate={float(0.6, 3.6, 9)}
    >
      <div className="w-9 h-9 bg-rose-100 rounded-lg flex items-center justify-center">
        <Receipt className="w-5 h-5 text-rose-600" />
      </div>
    </motion.div>

    {/* Stat card */}
    <motion.div
      className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-xl px-5 py-3 border border-slate-100"
      animate={pulse(0.4)}
    >
      <div className="flex items-center gap-3">
        <TrendingUp className="w-5 h-5 text-emerald-500" />
        <span className="text-sm font-semibold text-slate-700">{en ? '+32% client growth' : '+32% crescita clienti'}</span>
      </div>
    </motion.div>

    {/* Decorative dots */}
    {[
      'top-12 left-1/3',
      'bottom-32 right-1/4',
      'top-1/2 left-16',
    ].map((pos, i) => (
      <motion.div
        key={i}
        className={`absolute ${pos} w-2 h-2 bg-indigo-300 rounded-full opacity-40`}
        animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 2 + i, repeat: Infinity, delay: i * 0.5 }}
      />
    ))}
  </div>
  );
};

export default HeroIllustration;
